"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { copyTextToClipboard } from "@/lib/copy-to-clipboard";
import { cn } from "@/lib/utils";

interface ProcessInstanceIdCopyProps {
  processInstanceId: string;
  className?: string;
  textClassName?: string;
  showLabel?: boolean;
}

export function ProcessInstanceIdCopy({
  processInstanceId,
  className,
  textClassName,
  showLabel = true,
}: ProcessInstanceIdCopyProps) {
  const [copiedInstanceId, setCopiedInstanceId] = useState<string | null>(null);
  const copyFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const instanceIdTextRef = useRef<HTMLSpanElement | null>(null);
  const hasCopiedInstanceId = copiedInstanceId === processInstanceId;

  useEffect(() => {
    return () => {
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current);
      }
    };
  }, []);

  const handleCopyProcessInstanceId = async () => {
    try {
      await copyTextToClipboard(processInstanceId);
      setCopiedInstanceId(processInstanceId);
      toast.success("ID de instancia copiado");

      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current);
      }

      copyFeedbackTimerRef.current = setTimeout(() => {
        setCopiedInstanceId(null);
      }, 1500);
    } catch {
      if (instanceIdTextRef.current) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(instanceIdTextRef.current);
        selection?.removeAllRanges();
        selection?.addRange(range);
        toast.info("ID de instancia seleccionado");
        return;
      }

      toast.error("No se pudo copiar el ID de instancia");
    }
  };

  return (
    <div className={cn("flex min-w-0 items-center gap-1.5", className)}>
      <span
        ref={instanceIdTextRef}
        className={cn(
          "truncate font-mono text-xs text-muted-foreground",
          textClassName
        )}
      >
        {showLabel ? `Instancia: ${processInstanceId}` : processInstanceId}
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className={cn(
              "text-gray-400 hover:text-gray-900",
              hasCopiedInstanceId && "text-emerald-600 hover:text-emerald-700"
            )}
            onClick={handleCopyProcessInstanceId}
            aria-label="Copiar ID de instancia"
          >
            {hasCopiedInstanceId ? (
              <Check className="size-3" />
            ) : (
              <Copy className="size-3" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={6}>
          {hasCopiedInstanceId ? "Copiado" : "Copiar instancia"}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
